'use client'

import React, { Fragment } from 'react'
import {
  Dialog,
  Transition,
  TransitionChild,
  DialogPanel,
} from '@headlessui/react'

import './Modal.scss'

import Icon from 'shared/timber/v1/Icon'
import { DSSizes } from 'shared/timber/common/types'

import ModalIcon from './components/Icon'
import ModalTitle from './components/Title'
import ModalHeader from './components/Header'
import ModalFooter from './components/Footer'
import ModalContent from './components/Content'
import ModalDescription from './components/Description'

import classy from 'shared/utils/classy'

export type ModalSize =
  | Extract<
      DSSizes,
      'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl'
    >
  | 'fullscreen'

export interface ModalProps {
  open: boolean
  size?: ModalSize
  onClose?: (event?: React.MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
  showCloseButton?: boolean
  disableOutsideClick?: boolean
  'data-testid'?: string
  isVariableHeight?: boolean
  allowPanelOverflow?: boolean
  useSetPannelHeight?: boolean
  zIndex?: number
}

const Modal = ({
  open,
  size = 'md',
  onClose,
  children,
  showCloseButton = true,
  disableOutsideClick,
  isVariableHeight = false,
  allowPanelOverflow = false,
  useSetPannelHeight = false,
  zIndex,
  'data-testid': dataTestId = 'modal',
}: ModalProps) => {
  const handleClose = () => {
    if (!disableOutsideClick && onClose) onClose()
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        open={open}
        onClose={handleClose}
        className="Modal__Dialog"
        style={zIndex ? { zIndex } : undefined}
      >
        <TransitionChild
          as={Fragment}
          enter="Modal__Dialog--animation-enter"
          enterFrom="Modal__Dialog--animation-enter-from"
          enterTo="Modal__Dialog--animation-enter-to"
          leave="Modal__Dialog--animation-leave"
          leaveFrom="Modal__Dialog--animation-leave-from"
          leaveTo="Modal__Dialog--animation-leave-to"
        >
          <div className="Modal__Background" />
        </TransitionChild>

        <div className="Modal__Wrapper">
          <div className={classy('Modal__Content', `Modal__Content--${size}`)}>
            <TransitionChild
              as={Fragment}
              enter="Modal__Content--animation-enter"
              enterFrom="Modal__Content--animation-enter-from"
              enterTo="Modal__Content--animation-enter-to"
              leave="Modal__Content--animation-leave"
              leaveFrom="Modal__Content--animation-leave-from"
              leaveTo="Modal__Content--animation-leave-to"
            >
              <DialogPanel
                className={classy(
                  'Modal__Panel',
                  isVariableHeight && 'Modal__Panel--variable-height',
                  allowPanelOverflow && 'Modal__Panel--allow-overflow',
                  useSetPannelHeight && 'Modal__Panel--set-height',
                  `Modal__Panel--${size}`,
                )}
                data-testid={dataTestId}
              >
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="Modal__CloseButton"
                    data-testid="modal-close-button"
                  >
                    <span className="Modal__CloseButton__SrOlny">Close</span>
                    <Icon icon="x_mark" size="sm" />
                  </button>
                )}
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

Modal.Header = ModalHeader
Modal.Header.displayName = 'Modal.Header'

Modal.Icon = ModalIcon
Modal.Icon.displayName = 'Modal.Icon'

Modal.Title = ModalTitle
Modal.Title.displayName = 'Modal.Title'

Modal.Description = ModalDescription
Modal.Description.displayName = 'Modal.Description'

Modal.Content = ModalContent
Modal.Content.displayName = 'Modal.Content'

Modal.Footer = ModalFooter
Modal.Footer.displayName = 'Modal.Footer'

export default Modal
